import { formatTermLabel, type TriggerType } from "@canvas-sync/shared";
import { checkRunTerm, type ExpectedTerms } from "../lib/terms";

/**
 * The term(s) a sync's enrollment file came over with, shown short-form
 * first ("FA26 (2267)") since Canvas and the feed use the short description.
 * When the Canvas admin has entered expected terms on the Settings page and
 * this sync's term doesn't match the expectation for its schedule slot
 * (2 AM Pacific = secondary feed, every other hour = primary), the term
 * renders as an amber warning badge. Display-only — feed-term changes
 * require a ticket. Used by both the Syncs table and the sync detail page.
 */
export function TermDisplay({
  run,
  expectedTerms,
}: {
  run: { queuedAt: string; trigger: TriggerType; detectedTerms?: string[] };
  expectedTerms: ExpectedTerms;
}) {
  if (!run.detectedTerms || run.detectedTerms.length === 0) {
    return <span className="muted">—</span>;
  }
  const terms = run.detectedTerms.map(formatTermLabel).join(", ");
  const { slot, expected, mismatch } = checkRunTerm(run, expectedTerms);
  if (!mismatch) {
    return <span className="mono">{terms}</span>;
  }
  const slotLabel =
    slot === "secondary" ? "2 AM (secondary) feed" : "hourly (primary) feed";
  return (
    <span
      className="badge badge-warn"
      title={
        `This sync's file came over with term ${terms}, but the ${slotLabel} ` +
        `is expected to carry ${expected.map(formatTermLabel).join(" or ")}. ` +
        `Expected terms are display-only validation — submit a ticket for any feed term change.`
      }
    >
      {terms} ⚠
    </span>
  );
}
